import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { AxiosHeaders, AxiosError } from "axios";

import type {
  ActionCreateDto,
  ActionDto,
  AlertRuleCreateDto,
  AlertRuleDto,
  CommandCreateDto,
  DomainEventDto,
  CommandDto,
  DashboardDto,
  DashboardWidgetCreateDto,
  DashboardWidgetDto,
  DeviceChannelCreateDto,
  DeviceChannelDto,
  DeviceCreateDto,
  DeviceDto,
  DevicePageDto,
  DeviceUpdateDto,
  MetricCreateDto,
  MetricDto,
  TelemetryAggregatePointDto,
} from "@/lib/api/types";
import type { OtaHistoryEntryDto, OtaJobLifecycleStatus } from "@/lib/api/types/ota";
import { computeDeviceOperationalModel, DeviceOperationalStatus } from "@/lib/devices/device-state-model";
import { TENANT_HEADER } from "@/lib/auth/constants";
import { alertBindingFingerprintFromDataSource } from "@/modules/alerts/utils/alert-binding-fingerprint";
import type {
  MockBillingResponse,
  MockOrganizationRow,
  UserTenantMembershipDto as TenantMembershipDto,
  UserCreateBody,
  UserDto,
  UserUpdateBody,
} from "@/lib/api/types/enterprise";

import {
  MOCK_API_FAILURE_RATE,
  MOCK_DELAY_MS_MAX,
  MOCK_DELAY_MS_MIN,
  MOCK_TENANT_ID,
  MOCK_USER_ID,
} from "../constants";
import { isMockMode } from "../flags";
import { mockAlertDetail, MOCK_ALERTS, MOCK_ALERT_RULES } from "../data/alerts";
import { MOCK_BILLING } from "../data/billing";
import { MOCK_DEVICES } from "../data/devices";
import { MOCK_ORGANIZATIONS } from "../data/organizations";
import { MOCK_USERS } from "../data/users";
import { generateAggregatePoints, generateTelemetryRows } from "../generators/telemetry";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function mockLatency(): Promise<void> {
  const ms = MOCK_DELAY_MS_MIN + Math.random() * (MOCK_DELAY_MS_MAX - MOCK_DELAY_MS_MIN);
  await sleep(ms);
}

function shouldSimulateEmpty(): boolean {
  return Math.random() < 0.06;
}

function mockFormulaDependencyKeys(formula: string): string[] {
  const tokens = formula.match(/\b[a-zA-Z_][a-zA-Z0-9_]*\b/g) ?? [];
  const reserved = new Set(["avg", "min", "max"]);
  const keys = [...new Set(tokens.filter((t) => !reserved.has(t)))];
  return keys.length > 0 ? keys : ["temperature"];
}

function mockEvalFormulaProduct(points: TelemetryAggregatePointDto[], deps: string[]): (number | null)[] {
  return points.map((p) => {
    let acc = 1;
    for (const d of deps) {
      const x = p.values[d];
      if (x == null || typeof x !== "number" || Number.isNaN(x)) {
        return null;
      }
      acc *= x;
    }
    return acc;
  });
}

function maybeThrowMockFailure(config: InternalAxiosRequestConfig): void {
  if (Math.random() >= MOCK_API_FAILURE_RATE) {
    return;
  }
  const err = new AxiosError<{ detail?: string }>(
    "Mock intermittent failure (VITE_USE_MOCK)",
    "MOCK_FLAKY",
    config,
  );
  err.response = {
    status: 503,
    statusText: "Service Unavailable",
    data: { detail: "Simulated outage — adjust MOCK_API_FAILURE_RATE in lib/mock/constants.ts" },
    headers: {},
    config,
  };
  throw err;
}

/** In-memory copy for CRUD while mocking. */
let deviceTable: DeviceDto[] = MOCK_DEVICES.map((d) => ({
  ...d,
  metadata: {
    ...(d.metadata ? { ...d.metadata } : {}),
    transmission_mode:
      (d.metadata as { transmission_mode?: string } | null | undefined)?.transmission_mode ?? "mqtt",
    tags: Array.isArray((d.metadata as { tags?: unknown } | null | undefined)?.tags)
      ? ([...(d.metadata as { tags: string[] }).tags] as string[])
      : ["field", "monitoring"],
  } as DeviceDto["metadata"],
}));

const deviceChannelStore: Record<string, DeviceChannelDto[]> = {};

const otaHistoryStore: Record<string, OtaHistoryEntryDto[]> = {};

function normalizeOtaHistoryEntry(e: OtaHistoryEntryDto): OtaHistoryEntryDto {
  if (e.job_status) {
    return {
      ...e,
      progress_percent: e.progress_percent ?? null,
      queued_at: e.queued_at ?? e.created_at,
      sent_at: e.sent_at ?? null,
      acknowledged_at: e.acknowledged_at ?? null,
      started_at: e.started_at ?? null,
    };
  }
  const statusToJob: Record<string, OtaJobLifecycleStatus> = {
    pending: "pending",
    in_progress: "downloading",
    success: "installed",
    failed: "failed",
    rolled_back: "installed",
  };
  const job = statusToJob[e.status] ?? "pending";
  const progress =
    job === "installed"
      ? 100
      : job === "downloading"
        ? 60
        : job === "failed"
          ? 0
          : 0;

  return {
    ...e,
    job_status: job,
    progress_percent: progress,
    queued_at: e.created_at,

    sent_at: job === "pending" ? null : e.created_at,

    acknowledged_at:
      ["offered", "downloading", "installed", "failed"].includes(job)
        ? e.created_at
        : null,

    started_at:
      ["downloading", "installed", "failed"].includes(job)
        ? e.created_at
        : null,
  };
}

function makeOtaEntry(args: {
  deviceId: string;
  d: DeviceDto;
  to_version: string;
  channel: "stable" | "beta";
  legacyStatus: OtaHistoryEntryDto["status"];
  job_status: OtaJobLifecycleStatus;
  progress_percent: number | null;
  error_message: string | null;
  completed: boolean;
}): OtaHistoryEntryDto {
  const now = new Date().toISOString();
  const { job_status: js } = args;
  return {
    id: crypto.randomUUID(),
    device_id: args.deviceId,
    from_version: args.d.firmware_version ?? null,
    to_version: args.to_version,
    channel: args.channel,
    status: args.legacyStatus,
    job_status: js,
    progress_percent: args.progress_percent,
    queued_at: now,

    sent_at: js !== "pending" ? now : null,

    acknowledged_at:
      js === "offered" ||
      js === "downloading" ||
      js === "installed" ||
      js === "failed"
        ? now
        : null,

    started_at:
      js === "downloading" ||
      js === "installed" ||
      js === "failed"
        ? now
        : null,

    created_at: now,
    completed_at: args.completed ? now : null,
    error_message: args.error_message,
  };
}

/** Demo-grade timeline so the UI can show staged OTA lifecycle timestamps. */
function enrichCompletedSuccessOta(entry: OtaHistoryEntryDto): OtaHistoryEntryDto {
  if (entry.job_status !== "success" || !entry.completed_at) {
    return entry;
  }
  const t0 = new Date(entry.created_at).getTime();
  if (Number.isNaN(t0)) return entry;
  const iso = (ms: number) => new Date(ms).toISOString();
  return {
    ...entry,
    queued_at: iso(t0),
    sent_at: iso(t0 + 1500),
    acknowledged_at: iso(t0 + 3200),
    started_at: iso(t0 + 5100),
    completed_at: iso(t0 + 8800),
    progress_percent: 100,
  };
}

function withDeviceChannels(d: DeviceDto): DeviceDto {
  const ch = deviceChannelStore[d.id] ?? [];
  return { ...d, channels: ch.map((x) => ({ ...x })) };
}

function cloneDevices(): DeviceDto[] {
  return deviceTable.map((d) =>
    withDeviceChannels({
      ...d,
      metadata: d.metadata ? { ...d.metadata } : null,
    }),
  );
}

function persistMockChannels(deviceId: string, defs: DeviceChannelCreateDto[]): void {
  const now = new Date().toISOString();
  deviceChannelStore[deviceId] = defs.map((c) => ({
    id: crypto.randomUUID(),
    device_id: deviceId,
    key: c.key,
    label: c.label,
    unit: c.unit ?? null,
    data_type: c.data_type ?? "number",
    min_value: c.min_value ?? null,
    max_value: c.max_value ?? null,
    aggregation: c.aggregation ?? null,
    created_at: now,
  }));
}

const MOCK_DASHBOARD_ID = "44444444-4444-4444-4444-444444444401";

let mockDashboards: DashboardDto[] = [
  {
    id: MOCK_DASHBOARD_ID,
    tenant_id: MOCK_TENANT_ID,
    creator_user_id: MOCK_USER_ID,
    name: "Main dashboard",
    description: "Mock schema dashboard (empty widgets until you POST /widgets).",
    is_global: true,
    assigned_user_ids: [],
    layout: {},
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

let mockWidgets: DashboardWidgetDto[] = [];

const MOCK_METRIC_SEED_ID = "55555555-5555-5555-5555-555555555501";

const mockAutomationEvents: DomainEventDto[] = [];
const mockActionsStore: ActionDto[] = [];

let mockMetrics: MetricDto[] = [
  {
    id: MOCK_METRIC_SEED_ID,
    tenant_id: MOCK_TENANT_ID,
    name: "Apparent load (mock)",
    formula: "voltage * current",
    unit: "W",
    created_at: new Date().toISOString(),
  },
];

type MockOrganizationLocation = {
  id: string;
  organization_id: string;
  tenant_id: string;
  name: string;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  is_primary: boolean;
  note: string | null;
  created_at: string;
  updated_at: string;
};

let mockOrganizationLocations: MockOrganizationLocation[] = [
  {
    id: "86000000-0000-0000-0000-000000000001",
    organization_id: MOCK_ORGANIZATIONS[0]?.id ?? MOCK_TENANT_ID,
    tenant_id: MOCK_TENANT_ID,
    name: "Local Dev HQ",
    address_line_1: "1 Demo Way",
    address_line_2: null,
    city: "Mumbai",
    state: "MH",
    postal_code: "400001",
    country: "India",
    latitude: 18.9433,
    longitude: 72.8359,
    is_primary: true,
    note: "Primary workspace location",
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

/** In-memory users for mock CRUD (shape matches FastAPI `UserResponse`). */
let userTable: UserDto[] = MOCK_USERS.map((u) => ({ ...u }));

let tenantTable: TenantMembershipDto[] = [
  {
    id: MOCK_TENANT_ID,
    name: "Demo Workspace",
    domain: "demo.atech.local",
    owner_id: null,
    is_active: true,
  },
];

function syncUserAssignmentEmails(): void {
  const byId = new Map(userTable.map((u) => [u.id, u] as const));
  userTable = userTable.map((u) => {
    const admin = u.admin_user_id ? byId.get(u.admin_user_id) : null;
    const distributor = u.distributor_user_id ? byId.get(u.distributor_user_id) : null;
    return {
      ...u,
      admin_email: admin?.email ?? null,
      distributor_email: distributor?.email ?? null,
    };
  });
}

syncUserAssignmentEmails();

function mockTenantMembershipsForIds(tenantIds: string[]) {
  const uniqueIds = [...new Set(tenantIds.filter(Boolean))];
  return uniqueIds.map((id) => ({
    id,
    name: id === MOCK_TENANT_ID ? "Demo Workspace" : `Workspace ${id.slice(0, 8)}`,
    domain: null,
    is_active: true,
  }));
}

function userBelongsToTenant(user: UserDto, tenantId: string): boolean {
  const tenantIds = user.tenant_ids?.length ? user.tenant_ids : (user.tenant_id ? [user.tenant_id] : []);
  return tenantIds.includes(tenantId);
}

function validateUserHierarchy(
  tenantIds: string[],
  role: string,
  adminId: string | null | undefined,
  distributorId: string | null | undefined,
): string | null {
  if (role === "user") {
    if (!adminId && !distributorId) {
      return "At least one assignment is required for role User";
    }
    if (adminId) {
      const admin = userTable.find((u) => u.id === adminId);
      if (!admin || !tenantIds.some((tenantId) => userBelongsToTenant(admin, tenantId))) {
        return "Invalid admin assignment";
      }
      if (admin.role !== "admin") {
        return "Assigned admin must be an admin";
      }
    }
    if (distributorId) {
      const distributor = userTable.find((u) => u.id === distributorId);
      if (!distributor || !tenantIds.some((tenantId) => userBelongsToTenant(distributor, tenantId))) {
        return "Invalid distributor assignment";
      }
      if (distributor.role !== "distributor") {
        return "Assigned distributor must be a distributor";
      }
    }
    return null;
  }
  if (adminId != null || distributorId != null) {
    return "Only User role may have admin or distributor assignments";
  }
  return null;
}

function readTenantId(cfg: InternalAxiosRequestConfig, qp: Record<string, unknown>): string {
  const p = qp.tenant_id;
  if (typeof p === "string" && p.length > 0) {
    return p;
  }
  const headers = cfg.headers;
  if (headers && typeof (headers as AxiosHeaders).get === "function") {
    const v = (headers as AxiosHeaders).get(TENANT_HEADER);
    if (typeof v === "string" && v.length > 0) {
      return v;
    }
  }
  return MOCK_TENANT_ID;
}

function pathOnly(url: string | undefined): string {
  if (!url) {
    return "/";
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      return new URL(url).pathname;
    } catch {
      return url.split("?")[0] ?? "/";
    }
  }
  return url.split("?")[0] ?? "/";
}

function mockJsonResponse<T>(config: InternalAxiosRequestConfig, data: T, status = 200): AxiosResponse<T> {
  return {
    data,
    status,
    statusText: status === 204 ? "No Content" : "OK",
    headers: new AxiosHeaders({ "content-type": "application/json" }),
    config,
  };
}

function parseJsonBody(config: InternalAxiosRequestConfig): unknown {
  const d = config.data;
  if (d == null) {
    return null;
  }
  if (typeof d === "string") {
    try {
      return JSON.parse(d);
    } catch {
      return null;
    }
  }
  return d;
}

export function installMockApiIfEnabled(instance: AxiosInstance): void {
  if (!isMockMode()) {
    return;
  }

  instance.interceptors.request.use(async (config) => {
    const method = (config.method ?? "get").toUpperCase();
    const path = pathOnly(config.url);
    const params = (config.params ?? {}) as Record<string, unknown>;

    let payload: unknown = null;
    let status = 200;

    if (method === "GET" && path === "/users") {
      await mockLatency();
      maybeThrowMockFailure(config);
      const tid = params.tenant_id as string | undefined;
      let rows = userTable.map((u) => ({ ...u }));
      if (tid) {
        rows = rows.filter((u) => userBelongsToTenant(u, tid));
      }
      if (shouldSimulateEmpty()) {
        rows = [];
      }
      payload = { items: rows, total: rows.length };
    } else if (method === "GET" && /^\/users\/[^/]+$/.test(path)) {
      await mockLatency();
      maybeThrowMockFailure(config);
      const id = path.slice("/users/".length);
      const u = userTable.find((x) => x.id === id);
      if (!u) {
        status = 404;
        payload = { detail: "User not found" };
      } else {
        payload = { ...u };
      }
    } else if (method === "POST" && path === "/users") {
      await mockLatency();
      maybeThrowMockFailure(config);
      const body = parseJsonBody(config) as UserCreateBody | null;
      const tenantId = readTenantId(config, params);
      if (!body?.email?.trim() || !body?.name?.trim()) {
        status = 400;
        payload = { detail: "email and name are required" };
      } else if (userTable.some((u) => u.email === body.email.trim())) {
        status = 400;
        payload = { detail: "Email already registered" };
      } else {
        const shouldAutoCreateWorkspace = body.auto_create_workspace === true && body.role === "admin";
        const createdWorkspaceId = shouldAutoCreateWorkspace ? crypto.randomUUID() : null;
        const tenantIds = shouldAutoCreateWorkspace
          ? [createdWorkspaceId!]
          : (body.tenant_ids?.length ? body.tenant_ids : [tenantId]);
        const err = validateUserHierarchy(
          tenantIds,
          body.role,
          body.admin_user_id ?? null,
          body.distributor_user_id ?? null,
        );
        if (err) {
          status = 400;
          payload = { detail: err };
        } else {
          const row: UserDto = {
            id: crypto.randomUUID(),
            tenant_id: tenantIds[0] ?? tenantId,
            tenant_ids: tenantIds,
            tenants: mockTenantMembershipsForIds(tenantIds),
            email: body.email.trim(),
            name: body.name.trim(),
            role: body.role,
            account_status: "invited",
            is_active: body.is_active ?? true,
            organization_id: body.organization_id ?? null,
            organization_name: null,
            location_id: body.location_id ?? null,
            location_name: null,
            admin_user_id: body.role === "user" ? body.admin_user_id ?? null : null,
            admin_email: null,
            distributor_user_id: body.role === "user" ? body.distributor_user_id ?? null : null,
            distributor_email: null,
            invitation_sent_at: new Date().toISOString(),
            invitation_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            password_set_at: null,
            created_at: new Date().toISOString(),
          };
          if (shouldAutoCreateWorkspace && createdWorkspaceId) {
            tenantTable = [
              ...tenantTable,
              {
                id: createdWorkspaceId,
                name: `${row.name || row.email}'s workspace`,
                domain: null,
                owner_id: row.id,
                is_active: true,
                created_at: new Date().toISOString(),
              },
            ];
          }
          userTable = [...userTable, row];
          syncUserAssignmentEmails();
          const saved = userTable.find((x) => x.id === row.id)!;
          payload = { ...saved };
          status = 201;
        }
      }
    } else if (method === "PATCH" && /^\/users\/[^/]+$/.test(path)) {
      await mockLatency();
      maybeThrowMockFailure(config);
      const id = path.slice("/users/".length);
      const idx = userTable.findIndex((x) => x.id === id);
      const body = parseJsonBody(config) as UserUpdateBody | null;
      if (idx < 0) {
        status = 404;
        payload = { detail: "User not found" };
      } else {
        const prev = userTable[idx]!;
        const patch = body ?? {};
        const nextRole = patch.role ?? prev.role;
        const nextTenantIds = patch.tenant_ids?.length
          ? patch.tenant_ids
          : (prev.tenant_ids?.length ? prev.tenant_ids : (prev.tenant_id ? [prev.tenant_id] : [MOCK_TENANT_ID]));
        const nextAdmin =
          patch.admin_user_id !== undefined ? patch.admin_user_id : prev.admin_user_id;
        const nextDistributor =
          patch.distributor_user_id !== undefined ? patch.distributor_user_id : prev.distributor_user_id;
        const err = validateUserHierarchy(
          nextTenantIds,
          nextRole,
          nextRole === "user" ? nextAdmin : null,
          nextRole === "user" ? nextDistributor : null,
        );
        if (err) {
          status = 400;
          payload = { detail: err };
        } else {
          const nextOrganizationId =
            patch.organization_id !== undefined ? patch.organization_id : prev.organization_id;
          const nextLocationId = patch.location_id !== undefined ? patch.location_id : prev.location_id;
          const nextOrganization = nextOrganizationId
            ? MOCK_ORGANIZATIONS.find((organization) => organization.id === nextOrganizationId)
            : null;
          const nextLocation = nextLocationId
            ? mockOrganizationLocations.find((location) => location.id === nextLocationId)
            : null;
          const next: UserDto = {
            ...prev,
            tenant_id: nextTenantIds[0] ?? prev.tenant_id,
            tenant_ids: nextTenantIds,
            tenants: mockTenantMembershipsForIds(nextTenantIds),
            name: patch.name !== undefined ? patch.name : prev.name,
            is_active: patch.is_active !== undefined ? patch.is_active : prev.is_active,
            role: patch.role !== undefined ? patch.role : prev.role,
            organization_id: nextOrganizationId,
            organization_name: nextOrganization?.name ?? null,
            location_id: nextLocationId,
            location_name: nextLocation?.name ?? null,
            admin_user_id: nextRole === "user" ? nextAdmin : null,
            admin_email: prev.admin_email,
            distributor_user_id: nextRole === "user" ? nextDistributor : null,
            distributor_email: prev.distributor_email,
          };
          userTable = userTable.slice();
          userTable[idx] = next;
          syncUserAssignmentEmails();
          payload = { ...userTable[idx]! };
        }
      }
      } else if (method === "POST" && /^\/users\/[^/]+\/resend-invitation$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/users/".length, -"/resend-invitation".length);
        const idx = userTable.findIndex((x) => x.id === id);
        if (idx < 0) {
          status = 404;
          payload = { detail: "User not found" };
        } else if (userTable[idx]?.account_status !== "invited") {
          status = 400;
          payload = { detail: "Only invited users can receive a new invitation" };
        } else {
          const next = {
            ...userTable[idx]!,
            invitation_sent_at: new Date().toISOString(),
            invitation_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          };
          userTable = userTable.slice();
          userTable[idx] = next;
          payload = { ...next };
        }
      } else if (method === "DELETE" && /^\/users\/[^/]+$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/users/".length);
        const next = userTable.filter((u) => u.id !== id);
        if (next.length === userTable.length) {
          status = 404;
          payload = { detail: "User not found" };
        } else {
          userTable = next;
          syncUserAssignmentEmails();
          status = 204;
          payload = null;
        }
      } else if (method === "GET" && path === "/tenants") {
        await mockLatency();
        maybeThrowMockFailure(config);
        payload = tenantTable.map((tenant) => ({ ...tenant }));
      } else if (method === "POST" && path === "/tenants") {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as { name?: string; domain?: string | null; owner_id?: string; is_active?: boolean } | null;
        const owner = body?.owner_id ? userTable.find((user) => user.id === body.owner_id) : null;
        if (!body?.name?.trim()) {
          status = 400;
          payload = { detail: "name required" };
        } else if (!owner || owner.role === "super_admin") {
          status = 400;
          payload = { detail: "A non-super-admin owner is required" };
        } else {
          const row: TenantMembershipDto = {
            id: crypto.randomUUID(),
            name: body.name.trim(),
            domain: body.domain?.trim() ? body.domain.trim() : null,
            owner_id: owner.id,
            is_active: body.is_active ?? true,
          };
          tenantTable = [...tenantTable, row];
          owner.tenant_id = owner.tenant_id ?? row.id;
          owner.tenant_ids = Array.from(new Set([...(owner.tenant_ids ?? []), row.id]));
          payload = { ...row };
          status = 201;
        }
      } else if (method === "GET" && /^\/tenants\/[^/]+$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/tenants/".length);
        const tenant = tenantTable.find((t) => t.id === id);
        if (!tenant) {
          status = 404;
          payload = { detail: "Workspace not found" };
        } else {
          payload = { ...tenant };
        }
      } else if (method === "PUT" && /^\/tenants\/[^/]+$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/tenants/".length);
        const idx = tenantTable.findIndex((t) => t.id === id);
        const body = parseJsonBody(config) as { name?: string; domain?: string | null; is_active?: boolean } | null;
        if (idx < 0) {
          status = 404;
          payload = { detail: "Workspace not found" };
        } else {
          const prev = tenantTable[idx]!;
          const next: TenantMembershipDto = {
            ...prev,
            name: body?.name?.trim() || prev.name,
            domain: body?.domain === undefined ? prev.domain : (body.domain?.trim() ? body.domain.trim() : null),
            is_active: body?.is_active ?? prev.is_active,
          };
          tenantTable = tenantTable.map((row, rowIdx) => (rowIdx === idx ? next : row));
          payload = { ...next };
        }
      } else if (method === "PATCH" && /^\/tenants\/[^/]+\/owner$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/tenants/".length, -"/owner".length);
        const idx = tenantTable.findIndex((t) => t.id === id);
        const body = parseJsonBody(config) as { owner_id?: string | null } | null;
        const owner = body?.owner_id ? userTable.find((u) => u.id === body.owner_id) : null;
        if (idx < 0) {
          status = 404;
          payload = { detail: "Workspace not found" };
        } else if (!owner || owner.role === "super_admin" || !userBelongsToTenant(owner, id)) {
          status = 400;
          payload = { detail: "Owner must belong to the workspace" };
        } else {
          const next: TenantMembershipDto = {
            ...tenantTable[idx]!,
            owner_id: owner.id,
          };
          tenantTable = tenantTable.map((row, rowIdx) => (rowIdx === idx ? next : row));
          payload = { ...next };
        }
      } else if (method === "DELETE" && /^\/tenants\/[^/]+$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/tenants/".length);
        const next = tenantTable.filter((t) => t.id !== id);
        if (next.length === tenantTable.length) {
          status = 404;
          payload = { detail: "Workspace not found" };
        } else {
          tenantTable = next;
          status = 204;
          payload = null;
        }
      } else if (method === "GET" && path === "/organizations") {
        await mockLatency();
        maybeThrowMockFailure(config);
        let rows: MockOrganizationRow[] = MOCK_ORGANIZATIONS;
        if (shouldSimulateEmpty()) {
          rows = [];
        }
        payload = { items: rows, total: rows.length };
      } else if (method === "GET" && /^\/organizations\/[^/]+\/locations\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const organizationId = path.split("/")[2] ?? "";
        const rows = mockOrganizationLocations
          .filter((location) => location.organization_id === organizationId)
          .map((location) => ({ ...location }));
        payload = { items: rows, total: rows.length };
      } else if (method === "POST" && /^\/organizations\/[^/]+\/locations\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const organizationId = path.split("/")[2] ?? "";
        const body = parseJsonBody(config) as
          | {
              name?: string;
              address_line_1?: string | null;
              address_line_2?: string | null;
              city?: string | null;
              state?: string | null;
              postal_code?: string | null;
              country?: string | null;
              latitude?: number | null;
              longitude?: number | null;
              is_primary?: boolean;
              note?: string | null;
            }
          | null;
        if (!organizationId || !body?.name?.trim()) {
          status = 400;
          payload = { detail: "organization_id and name required" };
        } else {
          const now = new Date().toISOString();
          if (body.is_primary) {
            mockOrganizationLocations = mockOrganizationLocations.map((location) =>
              location.organization_id === organizationId
                ? { ...location, is_primary: false, updated_at: now }
                : location,
            );
          }
          const row: MockOrganizationLocation = {
            id: crypto.randomUUID(),
            organization_id: organizationId,
            tenant_id: MOCK_TENANT_ID,
            name: body.name.trim(),
            address_line_1: body.address_line_1 ?? null,
            address_line_2: body.address_line_2 ?? null,
            city: body.city ?? null,
            state: body.state ?? null,
            postal_code: body.postal_code ?? null,
            country: body.country ?? null,
            latitude: body.latitude ?? null,
            longitude: body.longitude ?? null,
            is_primary: body.is_primary ?? false,
            note: body.note ?? null,
            created_at: now,
            updated_at: now,
          };
          mockOrganizationLocations = [...mockOrganizationLocations, row];
          payload = row;
          status = 201;
        }
      } else if (method === "PUT" && /^\/organizations\/[^/]+\/locations\/[^/]+\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const organizationId = path.split("/")[2] ?? "";
        const locationId = path.split("/")[4] ?? "";
        const body = parseJsonBody(config) as Partial<MockOrganizationLocation> | null;
        const idx = mockOrganizationLocations.findIndex(
          (location) => location.id === locationId && location.organization_id === organizationId,
        );
        if (idx < 0) {
          status = 404;
          payload = { detail: "Location not found" };
        } else {
          const prev = mockOrganizationLocations[idx]!;
          const now = new Date().toISOString();
          if (body?.is_primary) {
            mockOrganizationLocations = mockOrganizationLocations.map((location) =>
              location.organization_id === organizationId && location.id !== locationId
                ? { ...location, is_primary: false, updated_at: now }
                : location,
            );
          }
          const next: MockOrganizationLocation = {
            ...prev,
            name: body?.name?.trim() || prev.name,
            address_line_1: body?.address_line_1 !== undefined ? body.address_line_1 ?? null : prev.address_line_1,
            address_line_2: body?.address_line_2 !== undefined ? body.address_line_2 ?? null : prev.address_line_2,
            city: body?.city !== undefined ? body.city ?? null : prev.city,
            state: body?.state !== undefined ? body.state ?? null : prev.state,
            postal_code: body?.postal_code !== undefined ? body.postal_code ?? null : prev.postal_code,
            country: body?.country !== undefined ? body.country ?? null : prev.country,
            latitude: body?.latitude !== undefined ? body.latitude ?? null : prev.latitude,
            longitude: body?.longitude !== undefined ? body.longitude ?? null : prev.longitude,
            is_primary: body?.is_primary ?? prev.is_primary,
            note: body?.note !== undefined ? body.note ?? null : prev.note,
            updated_at: now,
          };
          mockOrganizationLocations = mockOrganizationLocations.map((location, locationIdx) =>
            locationIdx === idx ? next : location,
          );
          payload = next;
        }
      } else if (method === "DELETE" && /^\/organizations\/[^/]+\/locations\/[^/]+\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const organizationId = path.split("/")[2] ?? "";
        const locationId = path.split("/")[4] ?? "";
        const before = mockOrganizationLocations.length;
        mockOrganizationLocations = mockOrganizationLocations.filter(
          (location) => !(location.id === locationId && location.organization_id === organizationId),
        );
        if (mockOrganizationLocations.length === before) {
          status = 404;
          payload = { detail: "Location not found" };
        } else {
          status = 204;
          payload = null;
        }
      } else if (method === "GET" && path === "/billing") {
        await mockLatency();
        maybeThrowMockFailure(config);
        let body: MockBillingResponse = MOCK_BILLING;
        if (Math.random() < 0.05) {
          body = { ...MOCK_BILLING, invoices: [] };
        }
        payload = body;
      } else if (method === "GET" && path === "/health") {
        await mockLatency();
        payload = {
          status: "healthy",
          checks: {
            database: { status: "healthy" },
            background_tasks: {
              "heartbeat-checker": { status: "running" },
              "buffer-drain": { status: "running" },
              "mqtt-consumer": { status: "running" },
              "websocket-consumer": { status: "running" },
            },
          },
        };
      } else if (method === "GET" && path === "/system-health") {
        await mockLatency();
        maybeThrowMockFailure(config);
        payload = {
          services: [
            {
              id: "85000000-0000-0000-0000-000000000001",
              service_name: "API server",
              status: "healthy",
              value: "99.98%",
              note: "2 instances active",
              sort_order: 1,
              updated_at: new Date().toISOString(),
            },
            {
              id: "85000000-0000-0000-0000-000000000002",
              service_name: "Database",
              status: "healthy",
              value: "43 ms",
              note: "Read latency p95",
              sort_order: 2,
              updated_at: new Date().toISOString(),
            },
            {
              id: "85000000-0000-0000-0000-000000000003",
              service_name: "Redis cache",
              status: "warning",
              value: "91%",
              note: "Memory pressure rising",
              sort_order: 3,
              updated_at: new Date().toISOString(),
            },
          ],
          incidents: [
            {
              id: "85200000-0000-0000-0000-000000000001",
              title: "Webhook retry spike",
              description: "Outbound retries increased on third-party callbacks.",
              severity: "degraded",
              occurred_at: new Date().toISOString(),
            },
          ],
          ingest_points: [
            {
              id: "85100000-0000-0000-0000-000000000001",
              sample_label: "00:00",
              sample_value: 1200,
              sort_order: 1,
              recorded_at: new Date().toISOString(),
            },
          ],
        };
      } else if (method === "GET" && /^\/devices\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tenantId = readTenantId(config, params);
        const limit = Math.min(200, Math.max(1, Number(params.limit ?? 50)));
        const offset = Math.max(0, Number(params.offset ?? 0));
        const search = String(params.search ?? "")
          .trim()
          .toLowerCase();
        const statusFilter = String(params.status ?? "")
          .trim()
          .toLowerCase();
        const protocolFilter = String(params.protocol ?? "")
          .trim()
          .toLowerCase();
        const siteFilter = String(params.site ?? "")
          .trim()
          .toLowerCase();
        const sortKey = String(params.sort ?? "last_seen").toLowerCase();
        const sortOrder = String(params.order ?? "desc").toLowerCase() === "asc" ? "asc" : "desc";

        const operationalTokens = new Set(["online", "offline", "stale", "error", "maintenance"]);

        let items = cloneDevices();
        if (tenantId && tenantId !== MOCK_TENANT_ID) {
          items = [];
        }
        if (search) {
          items = items.filter((d) => {
            const m = d.metadata as Record<string, unknown> | null | undefined;
            const rawTags = m?.tags;
            const tags = Array.isArray(rawTags) ? rawTags : [];
            const tagMatch = tags.some(
              (t) => typeof t === "string" && t.toLowerCase().includes(search),
            );
            return (
              (d.name?.toLowerCase().includes(search) ?? false) ||
              d.device_uid.toLowerCase().includes(search) ||
              d.id.toLowerCase().includes(search) ||
              tagMatch
            );
          });
        }
        if (statusFilter) {
          if (operationalTokens.has(statusFilter)) {
            items = items.filter(
              (d) => computeDeviceOperationalModel(d).status.toLowerCase() === statusFilter,
            );
          } else {
            items = items.filter((d) => (d.status ?? "").toLowerCase() === statusFilter);
          }
        }
        if (protocolFilter) {
          items = items.filter((d) => {
            const m = d.metadata as Record<string, unknown> | null | undefined;
            const p = String(m?.transmission_mode ?? m?.protocol ?? "")
              .toLowerCase()
              .replace(/-/g, "");
            return p.includes(protocolFilter.replace(/-/g, ""));
          });
        }
        if (siteFilter) {
          items = items.filter((d) => {
            const m = d.metadata as Record<string, unknown> | null | undefined;
            const s = String(m?.site ?? m?.location ?? "")
              .toLowerCase();
            return s.includes(siteFilter);
          });
        }
        if (shouldSimulateEmpty()) {
          items = [];
        }
        if (sortKey === "status" || sortKey === "health") {
          items = [...items].sort((a, b) => {
            const sa = computeDeviceOperationalModel(a).status;
            const sb = computeDeviceOperationalModel(b).status;
            return sortOrder === "asc" ? sa.localeCompare(sb) : sb.localeCompare(sa);
          });
        } else {
          items = [...items].sort((a, b) => {
            const ta = a.last_seen ? new Date(a.last_seen).getTime() : 0;
            const tb = b.last_seen ? new Date(b.last_seen).getTime() : 0;
            return sortOrder === "asc" ? ta - tb : tb - ta;
          });
        }
        const total = items.length;
        const page = items.slice(offset, offset + limit);
        const next_offset = offset + page.length < total ? offset + page.length : null;
        const body: DevicePageDto = {
          items: page,
          next_offset,
          total,
        };
        payload = body;
      } else if (method === "GET" && path.startsWith("/devices/")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const rest = path.slice("/devices/".length);
        const segments = rest.split("/").filter(Boolean);
        const id = segments[0] ?? "";
        if (segments[1] === "channels") {
          const exists = deviceTable.some((x) => x.id === id);
          if (!exists) {
            status = 404;
            payload = { detail: "Device not found" };
          } else {
            payload = [...(deviceChannelStore[id] ?? [])];
          }
        } else if (segments[1] === "ota" && segments[2] === "status") {
          const d = deviceTable.find((x) => x.id === id);
          if (!d) {
            status = 404;
            payload = { detail: "Device not found" };
          } else {
            const hist = (otaHistoryStore[id] ?? []).map(normalizeOtaHistoryEntry);
            payload = {
              current_firmware_version: d.firmware_version ?? null,
              last_job: hist[0] ?? null,
            };
          }
        } else if (segments[1] === "ota" && segments[2] === "history") {
          const d = deviceTable.find((x) => x.id === id);
          if (!d) {
            status = 404;
            payload = { detail: "Device not found" };
          } else {
            payload = [...(otaHistoryStore[id] ?? [])].map(normalizeOtaHistoryEntry);
          }
        } else if (segments.length === 1) {
          const d = cloneDevices().find((x) => x.id === id);
          if (!d) {
            status = 404;
            payload = { detail: "Device not found" };
          } else {
            payload = d;
          }
        } else {
          status = 404;
          payload = { detail: "Not found" };
        }
      } else if (method === "POST" && /^\/devices\/[^/]+\/channels\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const m = path.match(/^\/devices\/([^/]+)\/channels\/?$/);
        const deviceId = m?.[1] ?? "";
        const exists = deviceTable.some((x) => x.id === deviceId);
        const body = parseJsonBody(config) as DeviceChannelCreateDto | null;
        if (!exists) {
          status = 404;
          payload = { detail: "Device not found" };
        } else if (!body?.key?.trim() || !body.label?.trim()) {
          status = 400;
          payload = { detail: "key and label required" };
        } else {
          const list = [...(deviceChannelStore[deviceId] ?? [])];
          if (list.some((c) => c.key === body.key.trim())) {
            status = 400;
            payload = { detail: "duplicate key" };
          } else {
            const row: DeviceChannelDto = {
              id: crypto.randomUUID(),
              device_id: deviceId,
              key: body.key.trim(),
              label: body.label.trim(),
              unit: body.unit ?? null,
              data_type: body.data_type ?? "number",
              min_value: body.min_value ?? null,
              max_value: body.max_value ?? null,
              aggregation: body.aggregation ?? null,
              created_at: new Date().toISOString(),
            };
            deviceChannelStore[deviceId] = [...list, row];
            payload = row;
            status = 201;
          }
        }
      } else if (method === "POST" && /^\/devices\/[^/]+\/ota\/update\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const m = path.match(/^\/devices\/([^/]+)\/ota\/update\/?$/);
        const deviceId = m?.[1] ?? "";
        const d = deviceTable.find((x) => x.id === deviceId);
        const body = parseJsonBody(config) as {
          target_version?: string;
          channel?: string;
          force?: boolean;
        } | null;
        if (!d) {
          status = 404;
          payload = { detail: "Device not found" };
        } else if (!body?.target_version?.trim()) {
          status = 400;
          payload = { detail: "target_version required" };
        } else {
          const op = computeDeviceOperationalModel(d);
          const offline = op.status === DeviceOperationalStatus.OFFLINE;
          const force = body.force === true;
          const ch: "stable" | "beta" = body.channel === "beta" ? "beta" : "stable";
          const target = body.target_version.trim();
          if (offline && !force) {
            const entry = makeOtaEntry({
              deviceId,
              d,
              to_version: target,
              channel: ch,
              legacyStatus: "failed",
              job_status: "failed",
              progress_percent: 0,
              error_message: "Device offline — enqueue blocked (use force override)",
              completed: true,
            });
            otaHistoryStore[deviceId] = [entry, ...(otaHistoryStore[deviceId] ?? [])];
            payload = entry;
          } else {
            const entry = makeOtaEntry({
              deviceId,
              d,
              to_version: target,
              channel: ch,
              legacyStatus: "success",
              job_status: "installed",
              progress_percent: 100,
              error_message: null,
              completed: true,
            });
            const enriched = enrichCompletedSuccessOta(entry);
            otaHistoryStore[deviceId] = [enriched, ...(otaHistoryStore[deviceId] ?? [])];
            const idx = deviceTable.findIndex((x) => x.id === deviceId);
            if (idx >= 0) {
              const next: DeviceDto = {
                ...deviceTable[idx]!,
                firmware_version: enriched.to_version ?? null,
              };
              deviceTable = deviceTable.slice();
              deviceTable[idx] = next;
            }
            payload = enriched;
          }
        }
      } else if (method === "POST" && /^\/devices\/[^/]+\/ota\/schedule\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const m = path.match(/^\/devices\/([^/]+)\/ota\/schedule\/?$/);
        const deviceId = m?.[1] ?? "";
        const d = deviceTable.find((x) => x.id === deviceId);
        const body = parseJsonBody(config) as {
          target_version?: string;
          channel?: string;
          scheduled_at?: string;
        } | null;
        if (!d) {
          status = 404;
          payload = { detail: "Device not found" };
        } else if (!body?.target_version?.trim() || !body.scheduled_at) {
          status = 400;
          payload = { detail: "target_version and scheduled_at required" };
        } else {
          const entry = makeOtaEntry({
            deviceId,
            d,
            to_version: body.target_version.trim(),
            channel: body.channel === "beta" ? "beta" : "stable",
            legacyStatus: "pending",
            job_status: "pending",
            progress_percent: 0,
            error_message: null,
            completed: false,
          });
          otaHistoryStore[deviceId] = [entry, ...(otaHistoryStore[deviceId] ?? [])];
          payload = entry;
        }
      } else if (method === "POST" && /^\/devices\/[^/]+\/ota\/rollback\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const m = path.match(/^\/devices\/([^/]+)\/ota\/rollback\/?$/);
        const deviceId = m?.[1] ?? "";
        const d = deviceTable.find((x) => x.id === deviceId);
        if (!d) {
          status = 404;
          payload = { detail: "Device not found" };
        } else {
          const entry = makeOtaEntry({
            deviceId,
            d,
            to_version: d.firmware_version ?? "previous",
            channel: "stable",
            legacyStatus: "rolled_back",
            job_status: "installed",
            progress_percent: 100,
            error_message: null,
            completed: true,
          });
          otaHistoryStore[deviceId] = [entry, ...(otaHistoryStore[deviceId] ?? [])];
          payload = entry;
        }
      } else if (method === "POST" && /^\/devices\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as DeviceCreateDto | null;
        if (!body?.device_uid?.trim()) {
          status = 400;
          payload = { detail: "device_uid required" };
        } else if (deviceTable.some((d) => d.device_uid === body.device_uid)) {
          status = 400;
          payload = { detail: "device_uid already exists" };
        } else {
          const created: DeviceDto = {
            id: crypto.randomUUID(),
            name: body.name ?? null,
            device_uid: body.device_uid,
            status: "online",
            firmware_version: "2.0.0-mock",
            metadata: body.metadata ?? null,
            last_seen: new Date().toISOString(),
          };
          deviceTable = [...deviceTable, created];
          if (body.channels?.length) {
            persistMockChannels(created.id, body.channels);
          }
          payload = withDeviceChannels(created);
          status = 201;
        }
      } else if (method === "PATCH" && path.startsWith("/devices/")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/devices/".length).split("/")[0] ?? "";
        const idx = deviceTable.findIndex((x) => x.id === id);
        const body = (parseJsonBody(config) as DeviceUpdateDto | null) ?? {};
        if (idx < 0) {
          status = 404;
          payload = { detail: "Device not found" };
        } else {
          const prev = deviceTable[idx]!;
          const next: DeviceDto = {
            ...prev,
            name: body.name !== undefined ? body.name : prev.name,
            metadata: body.metadata !== undefined ? body.metadata : prev.metadata,
          };
          deviceTable = deviceTable.slice();
          deviceTable[idx] = next;
          payload = withDeviceChannels(next);
        }
      } else if (method === "DELETE" && path.startsWith("/devices/")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/devices/".length).split("/")[0] ?? "";
        const next = deviceTable.filter((d) => d.id !== id);
        if (next.length === deviceTable.length) {
          status = 404;
          payload = { detail: "Device not found" };
        } else {
          deviceTable = next;
          delete deviceChannelStore[id];
          status = 204;
          payload = null;
        }
      } else if (method === "PUT" && path.startsWith("/channels/")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const channelId = path.slice("/channels/".length).split("/")[0] ?? "";
        const body = (parseJsonBody(config) as Partial<DeviceChannelDto> | null) ?? {};
        let found: { deviceId: string; idx: number } | null = null;
        for (const [deviceId, rows] of Object.entries(deviceChannelStore)) {
          const idx = rows.findIndex((r) => r.id === channelId);
          if (idx >= 0) {
            found = { deviceId, idx };
            break;
          }
        }
        if (!found) {
          status = 404;
          payload = { detail: "Channel not found" };
        } else {
          const rows = [...(deviceChannelStore[found.deviceId] ?? [])];
          const prev = rows[found.idx]!;
          rows[found.idx] = {
            ...prev,
            key: typeof body.key === "string" ? body.key : prev.key,
            label: typeof body.label === "string" ? body.label : prev.label,
            unit: body.unit !== undefined ? (body.unit as string | null) : prev.unit,
            data_type: (body.data_type as DeviceChannelDto["data_type"]) ?? prev.data_type,
            min_value: body.min_value !== undefined ? (body.min_value as number | null) : prev.min_value,
            max_value: body.max_value !== undefined ? (body.max_value as number | null) : prev.max_value,
            aggregation:
              body.aggregation !== undefined ? (body.aggregation as string[] | null) : prev.aggregation,
          };
          deviceChannelStore[found.deviceId] = rows;
          payload = rows[found.idx];
        }
      } else if (method === "DELETE" && path.startsWith("/channels/")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const channelId = path.slice("/channels/".length).split("/")[0] ?? "";
        let removed = false;
        for (const [deviceId, rows] of Object.entries(deviceChannelStore)) {
          const next = rows.filter((r) => r.id !== channelId);
          if (next.length !== rows.length) {
            deviceChannelStore[deviceId] = next;
            removed = true;
            break;
          }
        }
        if (!removed) {
          status = 404;
          payload = { detail: "Channel not found" };
        } else {
          status = 204;
          payload = null;
        }
      } else if (method === "GET" && /^\/dashboards\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tid = readTenantId(config, params);
        if (!tid) {
          status = 400;
          payload = { detail: "tenant_id required" };
        } else if (tid !== MOCK_TENANT_ID) {
          payload = [];
        } else {
          payload = mockDashboards.map((d) => ({ ...d }));
        }
      } else if (method === "POST" && /^\/dashboards\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as {
          tenant_id?: string;
          name?: string;
          description?: string | null;
          is_global?: boolean;
          assigned_user_ids?: string[];
        } | null;
        const tenantId = body?.tenant_id ?? readTenantId(config, params);
        if (!tenantId || !body?.name?.trim()) {
          status = 400;
          payload = { detail: "tenant_id and name required" };
        } else {
          const now = new Date().toISOString();
          const row: DashboardDto = {
            id: crypto.randomUUID(),
            tenant_id: tenantId,
            creator_user_id: MOCK_USER_ID,
            name: body.name.trim(),
            description: body.description ?? null,
            is_global: body.is_global ?? false,
            assigned_user_ids: body.is_global ? [] : (body.assigned_user_ids ?? []),
            layout: {},
            created_at: now,
            updated_at: now,
          };
          mockDashboards = [...mockDashboards, row];
          payload = row;
          status = 201;
        }
      } else if (method === "GET" && /^\/dashboards\/[^/]+\/?$/.test(path) && !path.includes("/widgets")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.split("/")[2] ?? "";
        const d = mockDashboards.find((x) => x.id === id);
        if (!d) {
          status = 404;
          payload = { detail: "not found" };
        } else {
          payload = { ...d };
        }
      } else if (method === "PUT" && /^\/dashboards\/[^/]+\/?$/.test(path) && !path.includes("/widgets")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.split("/")[2] ?? "";
        const body = parseJsonBody(config) as {
          name?: string;
          description?: string | null;
          is_global?: boolean;
          assigned_user_ids?: string[];
        } | null;
        const idx = mockDashboards.findIndex((x) => x.id === id);
        if (idx < 0) {
          status = 404;
          payload = { detail: "not found" };
        } else {
          const prev = mockDashboards[idx]!;
          const next: DashboardDto = {
            ...prev,
            name: body?.name?.trim() || prev.name,
            description: body?.description === undefined ? prev.description : body.description,
            is_global: body?.is_global ?? prev.is_global,
            assigned_user_ids:
              (body?.is_global ?? prev.is_global) ? [] : (body?.assigned_user_ids ?? prev.assigned_user_ids),
            updated_at: new Date().toISOString(),
          };
          mockDashboards = mockDashboards.map((row, rowIdx) => (rowIdx === idx ? next : row));
          payload = next;
        }
      } else if (method === "DELETE" && /^\/dashboards\/[^/]+\/?$/.test(path) && !path.includes("/widgets")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.split("/")[2] ?? "";
        const exists = mockDashboards.some((x) => x.id === id);
        if (!exists) {
          status = 404;
          payload = { detail: "not found" };
        } else {
          mockDashboards = mockDashboards.filter((x) => x.id !== id);
          mockWidgets = mockWidgets.filter((w) => w.dashboard_id !== id);
          status = 204;
          payload = null;
        }
      } else if (method === "GET" && /^\/dashboards\/[^/]+\/widgets\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.split("/")[2] ?? "";
        if (!mockDashboards.some((x) => x.id === id)) {
          status = 404;
          payload = { detail: "not found" };
        } else {
          payload = mockWidgets.filter((w) => w.dashboard_id === id).map((w) => ({ ...w }));
        }
      } else if (method === "POST" && /^\/widgets\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as DashboardWidgetCreateDto | null;
        if (!body?.dashboard_id || !body.type) {
          status = 400;
          payload = { detail: "dashboard_id and type required" };
        } else if (!mockDashboards.some((d) => d.id === body.dashboard_id)) {
          status = 400;
          payload = { detail: "dashboard not found" };
        } else {
          const row: DashboardWidgetDto = {
            id: crypto.randomUUID(),
            dashboard_id: body.dashboard_id,
            type: body.type,
            config: body.config ?? null,
            position_x: body.position_x ?? 0,
            position_y: body.position_y ?? 0,
            width: body.width ?? 4,
            height: body.height ?? 2,
          };
          mockWidgets = [...mockWidgets, row];
          payload = row;
          status = 201;
        }
      } else if (method === "POST" && path === "/alerts/evaluate-scheduled") {
        await mockLatency();
        maybeThrowMockFailure(config);
        payload = { evaluated_rules: 0, fired: 0, resolved: 0 };
      } else if (method === "GET" && /^\/alerts\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        let rows = [...MOCK_ALERTS];
        const tid = params.tenant_id as string | undefined;
        const did = params.device_id as string | undefined;
        const st = params.status as string | undefined;
        const sev = params.severity as string | undefined;
        if (tid) {
          rows = rows.filter((a) => a.tenant_id === tid);
        }
        if (did) {
          rows = rows.filter((a) => a.device_id === did);
        }
        if (st) {
          rows = rows.filter((a) => (a.status ?? "active") === st);
        }
        if (sev) {
          rows = rows.filter((a) => (a.severity ?? "").toLowerCase() === sev.toLowerCase());
        }
        if (shouldSimulateEmpty()) {
          rows = [];
        }
        payload = rows;
      } else if (method === "GET" && path === "/alerts/summary/active") {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tid = params.tenant_id as string | undefined;
        const fps = new Set<string>();
        if (tid) {
          for (const a of MOCK_ALERTS) {
            if (a.tenant_id !== tid || (a.status ?? "active") !== "active") {
              continue;
            }
            const rule = a.rule_id ? MOCK_ALERT_RULES.find((r) => r.id === a.rule_id) : null;
            const raw = rule?.data_source;
            if (raw && typeof raw === "object" && !Array.isArray(raw)) {
              const fp = alertBindingFingerprintFromDataSource(raw as Record<string, unknown>);
              if (fp) {
                fps.add(fp);
              }
            }
          }
        }
        payload = { fingerprints: [...fps].sort() };
      } else if (method === "GET" && path.startsWith("/alerts/") && !path.includes("/rules")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/alerts/".length).split("/")[0] ?? "";
        const detail = mockAlertDetail(id);
        if (!detail) {
          status = 404;
          payload = { detail: "Alert not found" };
        } else {
          payload = detail;
        }
      } else if (method === "POST" && path.includes("/alerts/rules")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = (parseJsonBody(config) as AlertRuleCreateDto | null) ?? { name: "rule" };
        const rule: AlertRuleDto = {
          id: crypto.randomUUID(),
          tenant_id: body.tenant_id ?? MOCK_TENANT_ID,
          name: body.name,
          conditions: body.conditions ?? null,
          is_active: body.is_active ?? true,
          created_at: new Date().toISOString(),
          data_source: body.data_source ?? null,
          operator: body.operator ?? null,
          threshold: body.threshold ?? null,
          cooldown_minutes: body.cooldown_minutes ?? null,
          evaluation_window: body.evaluation_window ?? null,
          aggregation: body.aggregation ?? null,
          severity: body.severity ?? null,
          evaluation_mode: body.evaluation_mode ?? null,
        };
        MOCK_ALERT_RULES.push(rule);
        payload = rule;
        status = 201;
      } else if (method === "GET" && /^\/events\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tid = String(params.tenant_id ?? "");
        payload = mockAutomationEvents.filter((e) => e.tenant_id === tid);
      } else if (method === "GET" && /^\/actions\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tid = String(params.tenant_id ?? "");
        payload = mockActionsStore.filter((a) => a.tenant_id === tid);
      } else if (method === "POST" && /^\/actions\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as ActionCreateDto | null;
        if (!body?.tenant_id || !body.type) {
          status = 400;
          payload = { detail: "tenant_id and type required" };
        } else {
          const row: ActionDto = {
            id: crypto.randomUUID(),
            tenant_id: body.tenant_id,
            type: body.type,
            config: body.config ?? {},
            created_at: new Date().toISOString(),
          };
          mockActionsStore.push(row);
          payload = row;
          status = 201;
        }
      } else if (method === "GET" && /^\/metrics\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tenantId = String(params.tenant_id ?? "");
        if (!tenantId) {
          status = 400;
          payload = { detail: "tenant_id query parameter is required" };
        } else {
          payload = mockMetrics.filter((m) => m.tenant_id === tenantId);
        }
      } else if (method === "POST" && /^\/metrics\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as MetricCreateDto | null;
        if (!body?.tenant_id || !body.name?.trim() || !body.formula?.trim()) {
          status = 400;
          payload = { detail: "tenant_id, name, and formula are required" };
        } else {
          const row: MetricDto = {
            id: crypto.randomUUID(),
            tenant_id: body.tenant_id,
            name: body.name.trim(),
            formula: body.formula.trim(),
            unit: body.unit?.trim() ? body.unit.trim() : null,
            created_at: new Date().toISOString(),
          };
          mockMetrics = [...mockMetrics, row];
          payload = row;
          status = 201;
        }
      } else if (method === "POST" && path === "/telemetry/batch-query") {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as {
          tenant_id?: string;
          queries?: Array<{
            widget_id: string;
            device_id: string;
            channel?: string | null;
            formula?: string | null;
            metric_id?: string | null;
            time_range: string;
            aggregation: string;
          }>;
        } | null;
        if (!body?.tenant_id || !body.queries?.length) {
          status = 400;
          payload = { detail: "tenant_id and queries required" };
        } else {
          type BatchRow = {
            timestamps: string[];
            values: (number | null)[];
            bucket_seconds: number;
            error?: string | null;
          };
          const results: Record<string, BatchRow> = {};

          type Group = {
            device_id: string;
            time_range: string;
            aggregation: string;
            channels: Set<string>;
            raw: Map<string, string[]>;
            formulas: { widget_id: string; formula: string }[];
          };

          const groupKey = (q: { device_id: string; time_range: string; aggregation: string }) =>
            `${q.device_id}|${q.time_range}|${q.aggregation}`;

          const groups = new Map<string, Group>();
          for (const q of body.queries) {
            const key = groupKey(q);
            let g = groups.get(key);
            if (!g) {
              g = {
                device_id: q.device_id,
                time_range: q.time_range,
                aggregation: q.aggregation,
                channels: new Set<string>(),
                raw: new Map<string, string[]>(),
                formulas: [],
              };
              groups.set(key, g);
            }
            const ch = q.channel?.trim();
            if (ch) {
              g.channels.add(ch);
              const wids = g.raw.get(ch) ?? [];
              wids.push(q.widget_id);
              g.raw.set(ch, wids);
            } else {
              const eff =
                q.metric_id && mockMetrics.some((m) => m.id === q.metric_id)
                  ? (mockMetrics.find((m) => m.id === q.metric_id)?.formula ?? "temperature")
                  : (q.formula?.trim() ?? "temperature");
              const deps = mockFormulaDependencyKeys(eff);
              for (const d of deps) {
                g.channels.add(d);
              }
              g.formulas.push({ widget_id: q.widget_id, formula: eff });
            }
          }

          for (const g of groups.values()) {
            const durationMs =
              g.time_range === "last_1h"
                ? 3_600_000
                : g.time_range === "last_24h"
                  ? 86_400_000
                  : 7 * 86_400_000;
            const end = new Date();
            const start = new Date(end.getTime() - durationMs);
            const bucketSec = durationMs <= 3_600_000 ? 120 : durationMs <= 86_400_000 ? 300 : 3600;
            const metricList = [...g.channels];
            const agg = generateAggregatePoints(g.device_id, start, end, bucketSec, metricList, 4096);
            const pts = agg.points;

            for (const [ch, wids] of g.raw.entries()) {
              for (const wid of wids) {
                results[wid] = {
                  timestamps: pts.map((p) => p.t),
                  values: pts.map((p) => p.values[ch] ?? null),
                  bucket_seconds: agg.bucket_seconds,
                  error: null,
                };
              }
            }
            for (const job of g.formulas) {
              const deps = mockFormulaDependencyKeys(job.formula);
              results[job.widget_id] = {
                timestamps: pts.map((p) => p.t),
                values: mockEvalFormulaProduct(pts, deps),
                bucket_seconds: agg.bucket_seconds,
                error: null,
              };
            }
          }
          payload = { results };
        }
      } else if (method === "GET" && path === "/telemetry/aggregates") {
        await mockLatency();
        maybeThrowMockFailure(config);
        const deviceId = String(params.device_id ?? "");
        const start = new Date(String(params.start ?? ""));
        const end = new Date(String(params.end ?? ""));
        const bucket = Number(params.bucket_seconds ?? 300);
        const maxPoints = Math.min(8192, Number(params.max_points ?? 512));
        const metricsRaw = String(params.metrics ?? "temperature,humidity");
        const metrics = metricsRaw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        if (!deviceId || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
          status = 400;
          payload = { detail: "device_id, start, end required" };
        } else {
          payload = generateAggregatePoints(deviceId, start, end, bucket, metrics, maxPoints);
        }
      } else if (method === "GET" && /^\/telemetry\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const deviceId = String(params.device_id ?? "");
        const start = params.start ? new Date(String(params.start)) : new Date(Date.now() - 3_600_000);
        const end = params.end ? new Date(String(params.end)) : new Date();
        const metrics = ["temperature", "humidity"];
        if (!deviceId) {
          status = 400;
          payload = { detail: "device_id required" };
        } else {
          payload = generateTelemetryRows(deviceId, start, end, 60_000, metrics, 120);
        }
      } else if (method === "GET" && /^\/commands\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const tid = params.tenant_id as string | undefined;
        const did = params.device_id as string | undefined;
        let mockCmds: CommandDto[] = [
          {
            id: "cccccccc-cccc-cccc-cccc-cccccccccc01",
            device_id: MOCK_DEVICES[0]?.id ?? null,
            status: "acknowledged",
            response: { ok: true, echo: "mock" },
            command: { type: "ping" },
            created_at: new Date(Date.now() - 600_000).toISOString(),
          },
          {
            id: "cccccccc-cccc-cccc-cccc-cccccccccc02",
            device_id: MOCK_DEVICES[1]?.id ?? null,
            status: "pending",
            response: null,
            command: { type: "sample_rate", hz: 1 },
            created_at: new Date(Date.now() - 120_000).toISOString(),
          },
        ];
        if (tid && tid !== MOCK_TENANT_ID) {
          mockCmds = [];
        }
        payload = did ? mockCmds.filter((c) => c.device_id === did) : mockCmds;
      } else if (method === "GET" && path.startsWith("/commands/")) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const id = path.slice("/commands/".length).split("/")[0] ?? "";
        const list = [
          {
            id: "cccccccc-cccc-cccc-cccc-cccccccccc01",
            device_id: MOCK_DEVICES[0]?.id ?? null,
            status: "acknowledged",
            response: { ok: true },
            command: { type: "ping" },
            created_at: new Date().toISOString(),
          },
        ] as CommandDto[];
        const c = list.find((x) => x.id === id);
        if (!c) {
          status = 404;
          payload = { detail: "Command not found" };
        } else {
          payload = c;
        }
      } else if (method === "POST" && /^\/commands\/?$/.test(path)) {
        await mockLatency();
        maybeThrowMockFailure(config);
        const body = parseJsonBody(config) as CommandCreateDto | null;
        const c: CommandDto = {
          id: crypto.randomUUID(),
          device_id: body?.device_id ?? null,
          status: "pending",
          response: null,
          command: body?.command ?? {},
          created_at: new Date().toISOString(),
        };
        payload = c;
        status = 201;
      }

    if (payload !== null || status === 204) {
      (config as InternalAxiosRequestConfig & { __mock?: boolean }).adapter = async (cfg) => {
        if (status === 204) {
          return {
            data: undefined,
            status: 204,
            statusText: "No Content",
            headers: new AxiosHeaders(),
            config: cfg,
          };
        }
        return mockJsonResponse(cfg, payload, status);
      };
    }

    return config;
  });
}
